import React from 'react';
import SectionSurface from '../shared/SectionSurface';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

/* ─────────────────────────────────────────────────────────────────────────
 * EnterpriseAILayers, Three-layer stacked architecture diagram
 * Data Layer → Semantic Layer → Intelligent Application Layer
 * ───────────────────────────────────────────────────────────────────────── */

const LAYERS = [
  {
    num: '01',
    badge: 'badge-brand',
    numColor: 'text-brand-700',
    numBg: 'bg-brand-50 ring-1 ring-brand-200',
    borderAccent: 'border-l-brand-500',
    cardBg: 'bg-white',
    labelColor: 'text-brand-700',
    chipClass: 'badge-brand',
    name: 'Data Layer',
    verb: 'Connect · Migrate · Transform · Secure',
    desc:
      'Connect, migrate, transform, and secure data across operational systems, Postgres applications, Snowflake warehouses, BI platforms, object storage, and document repositories.',
    chips: [
      'ETL / ELT',
      'Snowflake migration',
      'Teradata migration',
      'dbt modernization',
      'HRIS · CRM · ERP',
      'Postgres integration',
      'transactional sources',
      'object storage',
      'document repositories',
      'warehouse signals',
      'storage signals',
      'cost data',
      'data quality',
      'security & access controls',
    ],
  },
  {
    num: '02',
    badge: 'badge-navy',
    numColor: 'text-brand-900',
    numBg: 'bg-[rgba(0,18,63,0.08)] ring-1 ring-[rgba(0,18,63,0.16)]',
    borderAccent: 'border-l-brand-900',
    cardBg: 'bg-white',
    labelColor: 'text-brand-900',
    chipClass: 'badge-navy',
    name: 'Semantic Layer',
    verb: 'Define · Govern · Contextualize · Remember',
    desc:
      'Define, govern, contextualize, and retrieve meaning across metrics, relationships, business rules, documents, policies, and domain knowledge.',
    chips: [
      'semantic modeling',
      'metric definitions',
      'ontology design',
      'business glossary',
      'governed KPI logic',
      'metric catalog',
      'domain memory',
      'document context',
      'Cortex Analyst',
      'Cortex Search indexes',
      'governed retrieval',
      'semantic search',
      'Ossie alignment',
      'define once, consume everywhere',
    ],
  },
  {
    num: '03',
    badge: 'badge-accent',
    numColor: 'text-accent-700',
    numBg: 'bg-accent-50 ring-1 ring-accent-200',
    borderAccent: 'border-l-accent-500',
    cardBg: 'bg-white',
    labelColor: 'text-accent-700',
    chipClass: 'badge-accent',
    name: 'Intelligent Application Layer',
    verb: 'Converse · Monitor · Reason · Update · Act',
    desc:
      'Build React, Java, Streamlit, BI, and agentic applications that let users converse, monitor, search, reason, notify, and apply approved writes on trusted enterprise context.',
    chips: [
      'conversational analytics',
      'Cortex agents',
      'Snowflake MCP',
      'chat-operated writes',
      'governed seeds',
      'Streamlit apps',
      'React apps',
      'Java services',
      'document-aware AI assistants',
      'Power BI · Tableau · QuickSight',
      'dashboard marketplace',
      'decision intelligence agents',
      'KPI monitoring',
      'smart notifications',
      'anomaly detection',
      'moving std-dev alerts',
      'cost variance alerts',
      'warehouse comparisons',
      'automated briefings',
      'agentic workflows',
    ],
  },
];

function ArrowConnector() {
  return (
    <div className="-my-px flex items-center justify-center py-0 relative z-10">
      <div className="flex flex-col items-center gap-0">
        <div
          className="h-7 w-[2px] rounded-full bg-gradient-to-b from-brand-300 to-cyan-300 shadow-[0_0_8px_rgba(56,189,248,0.55)]"
        />
        <svg
          width={20}
          height={14}
          viewBox="0 0 20 14"
          fill="none"
          className="-mt-0.5 shrink-0 drop-shadow-[0_0_8px_rgba(56,189,248,0.9)]"
          style={{ overflow: 'visible' }}
        >
          <path d="M10 14L1 1L19 1L10 14Z" className="fill-cyan-300" />
        </svg>
      </div>
    </div>
  );
}

export default function EnterpriseAILayers() {
  return (
    <SectionSurface
      variant="default"
      decor="none"
      bleed="none"
        style={{ background: 'rgba(240, 250, 255, 0.6)' }}
    >
      <div className="container-x py-12 sm:py-16 lg:py-20">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center mb-12">
          <p className="eyebrow text-brand-300">Enterprise AI Architecture</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl lg:text-5xl">
                Three Layers. One Enterprise AI Ecosystem.
          </h2>
          <p className="mt-4 text-sm leading-7 text-ink-600 sm:text-base">
            We connect your data foundation, semantic layer, and intelligent applications so teams can move from
            fragmented reporting to governed, agentic decision-making.
          </p>
        </div>

        {/* Three-layer stacked diagram */}
        <div className="mx-auto max-w-4xl">
          {LAYERS.map((layer, idx) => (
            <div key={layer.num}>
              {/* Layer card */}
              <div
                className={`relative rounded-2xl border-l-4 ${layer.borderAccent} ${layer.cardBg} p-6 ring-1 ring-ink-200/80 shadow-md sm:p-8`}
              >
                <div className="flex flex-col gap-5 sm:flex-row sm:gap-8">
                  {/* Layer number + name */}
                  <div className="shrink-0 flex flex-row sm:flex-col items-center sm:items-start gap-3 sm:gap-2 sm:w-44">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl text-lg font-bold ${layer.numBg} ${layer.numColor}`}>
                      {layer.num}
                    </span>
                    <div>
                      <p className={`text-xs font-bold uppercase tracking-widest ${layer.labelColor}`}>{layer.name}</p>
                      <p className="mt-0.5 text-xs font-semibold text-ink-800 leading-snug">{layer.verb}</p>
                    </div>
                  </div>

                  {/* Description + chips */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-6 text-ink-900">{layer.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {layer.chips.map((chip) => (
                        <span
                          key={chip}
                          className={`${layer.chipClass} text-xs font-semibold`}
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow between layers */}
              {idx < LAYERS.length - 1 && <ArrowConnector />}
            </div>
          ))}

          {/* Bottom callout */}
          <div className="mt-8 rounded-2xl bg-blue-50 ring-1 ring-blue-100 p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
              <div className="shrink-0">
                    <span className="inline-flex items-center gap-2 rounded-full bg-brand-600/10 px-3 py-1 text-xs font-semibold text-brand-600 ring-1 ring-brand-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
                  Built on Snowflake
                </span>
              </div>
              <div className="space-y-3 min-w-0">
                    <p className="text-sm font-semibold text-ink-900">
                  Built on Snowflake. Designed for interoperability.
                </p>
                <p className="text-sm leading-6 text-ink-700">
                      Much of the intelligent application layer can be built directly on Snowflake infrastructure using
                  Cortex, Cortex Analyst, Cortex Search, Streamlit, Snowflake Native Apps, Tasks, Alerts, Dynamic
                  Tables, secure views, semantic models, and API integrations. From Cortex and Streamlit to semantic
                  models and Ossie alignment, MyOrgLab builds on your existing enterprise
                  platforms instead of replacing them.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row sm:gap-0 sm:divide-x sm:divide-ink-200">
                  <p className="text-sm text-ink-600 sm:pr-5">
                      <span className="font-medium text-ink-700">The application layer is no longer just where users view data.</span>{' '}
                    It is where agents, workflows, and business users act on trusted semantic context.
                  </p>
                  <p className="text-sm text-ink-600 sm:pl-5">
                      <span className="font-medium text-ink-700">Smart notifications should reduce noise, not create it.</span>{' '}
                    We focus on meaningful signals, governed thresholds, and business context before alerting users.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA row */}
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a href="/platform-engineering" className="btn-primary btn-lg">
              Explore our platform
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </a>
            <a href="/contact" className="btn-secondary btn-lg">
              Start a conversation <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </SectionSurface>
  );
}
